import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { ArrowLeft, ArrowRight, Check, Save, Building, Users, Package, Settings, BarChart3, MessageSquare, Database, FolderOpen } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { useQueryClient } from '@tanstack/react-query'

// Import step components (we'll create these next)
import { ClientCoreInfoStep } from './steps/ClientCoreInfoStep'
import { ClientTeamStep } from './steps/ClientTeamStep'
import { ClientProductStep } from './steps/ClientProductStep'
import { ClientInfrastructureStep } from './steps/ClientInfrastructureStep'
import { ClientTAMStep } from './steps/ClientTAMStep'
import { ClientCRMStep } from './steps/ClientCRMStep'
import { ClientSlackStep } from './steps/ClientSlackStep'
import { ClientAssetsStep } from './steps/ClientAssetsStep'

import type { 
  Client, 
  ClientWizardForm, 
  ClientCoreInfoForm,
  ClientTeamForm,
  ClientProductForm,
  ClientInfrastructureForm,
  ClientTAMForm,
  ClientCRMForm,
  ClientSlackForm,
  ClientAssetsForm
} from '@/types/client'

interface ClientWizardProps {
  client?: Client
  // Values-only seed for the create flow: pre-fills the form from an intake
  // submission WITHOUT switching the wizard into edit mode (that stays keyed
  // on `client`/`currentClient`). Ignored when `client` is set.
  intakePrefill?: Client
  onComplete?: (client: Client) => void
  onCancel?: () => void
}

const STEPS = [
  {
    id: 'coreInfo',
    title: 'Core Information',
    description: 'Basic client details and package information',
    icon: Building,
    component: ClientCoreInfoStep
  },
  {
    id: 'product',
    title: 'Products & Territories',
    description: 'Product information and target market',
    icon: Package,
    component: ClientProductStep
  },
  {
    id: 'team',
    title: 'Team & Contacts',
    description: 'Contact information and team details',
    icon: Users,
    component: ClientTeamStep
  },
  {
    id: 'infrastructure',
    title: 'Infrastructure',
    description: 'Domains, mailboxes, and LinkedIn accounts',
    icon: Settings,
    component: ClientInfrastructureStep
  },
  {
    id: 'crm',
    title: 'CRM & Tech Stack',
    description: 'CRM systems and technical integrations',
    icon: Database,
    component: ClientCRMStep
  },
  {
    id: 'slack',
    title: 'Slack Channels',
    description: 'Slack channel configuration',
    icon: MessageSquare,
    component: ClientSlackStep
  },
  {
    id: 'assets',
    title: 'Assets/Docs',
    description: 'Case studies, demos, and sales collateral',
    icon: FolderOpen,
    component: ClientAssetsStep
  },
  {
    id: 'tam',
    title: 'TAM & Blocklist',
    description: 'Target account lists and segmentation',
    icon: BarChart3,
    component: ClientTAMStep
  }
]

export function ClientWizard({ client, intakePrefill, onComplete, onCancel }: ClientWizardProps) {
  const { profile } = useAuth()
  const queryClient = useQueryClient()
  const [currentStep, setCurrentStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set())
  // Track the current client (either passed as prop or newly created)
  const [currentClient, setCurrentClient] = useState<Client | null>(client || null)

  // Form seed: an existing client (edit mode) wins; otherwise an intake
  // prefill seeds the initial values for the create flow.
  const seed = client ?? intakePrefill ?? null

  // Initialize form data - map database fields to form fields
  // Parse notes field inline to avoid issues with variable scope
  const [formData, setFormData] = useState<ClientWizardForm>(() => {
    // Parse notes field if it contains JSON data
    let notesData: any = {}
    if (seed?.notes) {
      try {
        notesData = JSON.parse(seed.notes)
      } catch {
        notesData = {}
      }
    }
    
    return {
      coreInfo: {
      name: seed?.name || '',
      company: seed?.company || '',
      website: seed?.website || notesData.website || seed?.domain || '',
      onboarding_notes: seed?.onboarding_notes || notesData.onboarding_notes || '',
      package_signed: seed?.package_signed || notesData.package_signed || null,
      tam_target_size: seed?.tam_target_size || notesData.tam_target_size || null,
      clickup_backlog_url: seed?.clickup_backlog_url || '',
      attribution_window_days: seed?.attribution_window_days || notesData.attribution_window_days || 90,
      // Retainer + lifecycle (migration 20260420150000). Cast client to any
      // because the shared Client type doesn't yet carry these 5 columns on
      // every fetched row — they were added via raw migration and the
      // auto-generated supabase types haven't been regenerated.
      monthly_retainer_usd: (seed as any)?.monthly_retainer_usd ?? null,
      retainer_currency: (seed as any)?.retainer_currency ?? null,
      onboarding_date: (seed as any)?.onboarding_date ?? null,
      attio_record_id: (seed as any)?.attio_record_id ?? null,
    },
    team: {
      main_contact_name: notesData.main_contact_name || '',
      main_contact_email: seed?.main_contact_email || '',
      main_contact_phone: seed?.phone || notesData.main_contact_phone || '',
      secondary_contact_name: notesData.secondary_contact_name || '',
      secondary_contact_email: seed?.secondary_contact_email || notesData.secondary_contact_email || '',
      secondary_contact_phone: notesData.secondary_contact_phone || '',
      billing_name: seed?.billing_name || notesData.billing_name || '',
      billing_email: seed?.billing_email || notesData.billing_email || '',
      sdr_count: seed?.sdrs_count || notesData.sdr_count || 0,
      ae_count: seed?.ae_count || notesData.ae_count || 0,
      meeting_links: seed?.meeting_links || notesData.meeting_links || {},
      sales_team_emails: seed?.sales_team_emails || notesData.sales_team_emails || [],
      sales_team_names: seed?.sales_team_names || notesData.sales_team_names || [],
      sales_team_roles: seed?.sales_team_roles || notesData.sales_team_roles || [],
      sales_team_regions: seed?.sales_team_regions || notesData.sales_team_regions || [],
      sales_team_calendly_links: seed?.sales_team_calendly_links || notesData.sales_team_calendly_links || [],
      // Structured team members and assignments (keep reading from notes as complex nested objects)
      team_members: notesData.team_members || [],
      assignments: notesData.assignments || []
    },
    product: {
      product_names: seed?.products || notesData.product_names || [],
      products: notesData.products || [],
      territories: notesData.territories || [],
      product_categories: seed?.product_categories || notesData.product_categories || '',
      product_description: seed?.product_description || notesData.product_description || '',
      icp_titles: seed?.icp_titles || notesData.icp_titles || [],
      icp_departments: seed?.icp_departments || notesData.icp_departments || [],
      geos: seed?.geos || notesData.geos || [],
      compatibility_requirements: seed?.compatibility_requirements || notesData.compatibility_requirements || '',
      suitability_requirements: seed?.suitability_requirements || notesData.suitability_requirements || ''
    },
    infrastructure: {
      domains_included: seed?.domains_included || notesData.domains_included || [],
      mailboxes_included: seed?.mailboxes_included || notesData.mailboxes_included || [],
      linkedin_accounts_count: seed?.linkedin_count || notesData.linkedin_accounts_count || 0,
      cold_calling_included: seed?.cold_calling_included ?? notesData.cold_calling_included ?? false,
      reply_handling: seed?.reply_handling || notesData.reply_handling || null,
      pricing_data: notesData.pricing_data || undefined // Persisted pricing/availability data from Spaceship checks
    },
    tam: {
      tam_accounts_url: seed?.tam_accounts_url || seed?.tam_list_url || notesData.tam_accounts_url || '',
      tam_contacts_url: seed?.tam_contacts_url || seed?.blocklist_url || notesData.tam_contacts_url || '',
      tam_segments: seed?.tam_segments || notesData.tam_segments || {},
      tam_uploaded_data: seed?.tam_uploaded_data || notesData.tam_uploaded_data || {},
      // Clay table URLs - with backward compatibility for old field names
      meetings_scheduled_url: notesData.meetings_scheduled_url || notesData.discovery_calls_url || '',
      qualified_meetings_url: notesData.qualified_meetings_url || notesData.demo_calls_url || '',
      deals_won_url: notesData.deals_won_url || '',
      accounts_owned_url: notesData.accounts_owned_url || notesData.deals_owned_url || '',
      demos_requested_url: notesData.demos_requested_url || notesData.request_demos_url || '',
      current_customers_url: notesData.current_customers_url || '',
      customer_signups_url: notesData.customer_signups_url || '',
      churned_customers_url: notesData.churned_customers_url || '',
      dnc_blocklist_url: notesData.dnc_blocklist_url || ''
    },
    crm: {
      crm_system: seed?.crm_system || notesData.crm_system || '',
      crm_credentials: seed?.crm_credentials || notesData.crm_credentials || {},
      sequencer_system: seed?.sequencer_system || notesData.sequencer_system || '',
      sequencer_credentials: seed?.sequencer_credentials || notesData.sequencer_credentials || {},
      enrichment_tools: seed?.enrichment_tools || notesData.enrichment_tools || [],
      enrichment_credentials: seed?.enrichment_credentials || notesData.enrichment_credentials || {}
    },
    slack: (() => {
      // Backward compatibility: Migrate old single-channel structure to new multi-channel structure
      // IMPORTANT: Read from database columns first (where Edge Function saves), then fall back to notesData for legacy support
      const legacySalesChannel = seed?.slack_sales_channel || notesData.slack_sales_channel || ''
      const legacyPositiveReplies = seed?.slack_positive_replies_channel || notesData.slack_positive_replies_channel || ''
      const legacyNeutralNegative = seed?.slack_neutral_negative_replies_channel || notesData.slack_neutral_negative_replies_channel || ''
      
      // If new structure exists, use it; otherwise migrate from legacy
      let salesChannels = notesData.slack_sales_channels || []
      let positiveReplyChannels = notesData.slack_positive_reply_channels || []
      let neutralNegativeChannels = notesData.slack_neutral_negative_reply_channels || []
      
      // Migrate legacy single channels to new structure if they exist and new structure is empty
      if (salesChannels.length === 0 && legacySalesChannel) {
        salesChannels = [{
          id: `legacy-sales-${Date.now()}`,
          name: legacySalesChannel,
          team_member_ids: [],
          status: legacySalesChannel ? 'created' : 'not_set'
        }]
      }
      
      if (positiveReplyChannels.length === 0 && legacyPositiveReplies) {
        positiveReplyChannels = [{
          id: `legacy-positive-${Date.now()}`,
          name: legacyPositiveReplies,
          team_member_ids: [],
          status: legacyPositiveReplies ? 'created' : 'not_set'
        }]
      }
      
      if (neutralNegativeChannels.length === 0 && legacyNeutralNegative) {
        neutralNegativeChannels = [{
          id: `legacy-neutral-${Date.now()}`,
          name: legacyNeutralNegative,
          team_member_ids: [],
          status: legacyNeutralNegative ? 'created' : 'not_set'
        }]
      }
      
      return {
        slack_main_channel: seed?.slack_main_channel || '',
        slack_main_team_member_ids: notesData.slack_main_team_member_ids || [],
        slack_reporting_channel: seed?.slack_reporting_channel || '',
        slack_reporting_team_member_ids: notesData.slack_reporting_team_member_ids || [],
        slack_sales_channels: salesChannels,
        slack_positive_reply_channels: positiveReplyChannels,
        slack_neutral_negative_reply_channels: neutralNegativeChannels,
        // Legacy fields for backward compatibility (read from columns first, then notes)
        slack_sales_channel: seed?.slack_sales_channel || legacySalesChannel,
        slack_positive_replies_channel: seed?.slack_positive_replies_channel || legacyPositiveReplies,
        slack_neutral_negative_replies_channel: seed?.slack_neutral_negative_replies_channel || legacyNeutralNegative
      }
    })(),
    assets: {
      case_studies_url: notesData.case_studies_url || '',
      demo_recordings_url: notesData.demo_recordings_url || '',
      sales_collateral_url: notesData.sales_collateral_url || '',
      other_assets_url: notesData.other_assets_url || '',
      assets_notes: notesData.assets_notes || ''
    }
    }
  })

  // Update formData when client prop changes (e.g., after navigation or query refresh)
  useEffect(() => {
    if (client) {
      // Only update if client prop has changed (new client or refreshed data)
      if (!currentClient || client.id !== currentClient.id || client.updated_at !== currentClient.updated_at) {
        // Client prop changed - update formData to reflect latest database values
        let notesData: any = {}
        if (client.notes) {
          try {
            notesData = JSON.parse(client.notes)
          } catch {
            notesData = {}
          }
        }
        
        setFormData(prev => ({
          ...prev,
          coreInfo: {
            ...prev.coreInfo,
            name: client.name || prev.coreInfo.name,
            company: client.company || prev.coreInfo.company,
            website: client.website || notesData.website || client.domain || prev.coreInfo.website,
            onboarding_notes: client.onboarding_notes || notesData.onboarding_notes || prev.coreInfo.onboarding_notes,
            package_signed: client.package_signed || notesData.package_signed || prev.coreInfo.package_signed,
            tam_target_size: client.tam_target_size || notesData.tam_target_size || prev.coreInfo.tam_target_size,
            clickup_backlog_url: client.clickup_backlog_url || prev.coreInfo.clickup_backlog_url,
            // Retainer + lifecycle — use nullish coalescing so an explicit 0
            // or empty string stays, and only `undefined` falls back.
            monthly_retainer_usd: (client as any).monthly_retainer_usd ?? prev.coreInfo.monthly_retainer_usd,
            retainer_currency: (client as any).retainer_currency ?? prev.coreInfo.retainer_currency,
            onboarding_date: (client as any).onboarding_date ?? prev.coreInfo.onboarding_date,
            attio_record_id: (client as any).attio_record_id ?? prev.coreInfo.attio_record_id,
          },
          team: {
            ...prev.team,
            main_contact_email: client.main_contact_email || prev.team.main_contact_email,
            main_contact_phone: client.phone || notesData.main_contact_phone || prev.team.main_contact_phone,
            secondary_contact_email: client.secondary_contact_email || notesData.secondary_contact_email || prev.team.secondary_contact_email,
            billing_name: client.billing_name || notesData.billing_name || prev.team.billing_name,
            billing_email: client.billing_email || notesData.billing_email || prev.team.billing_email,
            sdr_count: client.sdrs_count || notesData.sdr_count || prev.team.sdr_count,
            ae_count: client.ae_count || notesData.ae_count || prev.team.ae_count,
            meeting_links: client.meeting_links || notesData.meeting_links || prev.team.meeting_links,
            sales_team_emails: client.sales_team_emails || notesData.sales_team_emails || prev.team.sales_team_emails,
            sales_team_names: client.sales_team_names || notesData.sales_team_names || prev.team.sales_team_names,
            sales_team_roles: client.sales_team_roles || notesData.sales_team_roles || prev.team.sales_team_roles,
            sales_team_regions: client.sales_team_regions || notesData.sales_team_regions || prev.team.sales_team_regions,
            sales_team_calendly_links: client.sales_team_calendly_links || notesData.sales_team_calendly_links || prev.team.sales_team_calendly_links
          },
          product: {
            ...prev.product,
            product_names: client.products || notesData.product_names || prev.product.product_names,
            product_categories: client.product_categories || notesData.product_categories || prev.product.product_categories,
            product_description: client.product_description || notesData.product_description || prev.product.product_description,
            icp_titles: client.icp_titles || notesData.icp_titles || prev.product.icp_titles,
            icp_departments: client.icp_departments || notesData.icp_departments || prev.product.icp_departments,
            geos: client.geos || notesData.geos || prev.product.geos,
            compatibility_requirements: client.compatibility_requirements || notesData.compatibility_requirements || prev.product.compatibility_requirements,
            suitability_requirements: client.suitability_requirements || notesData.suitability_requirements || prev.product.suitability_requirements
          },
          infrastructure: {
            ...prev.infrastructure,
            domains_included: client.domains_included || notesData.domains_included || prev.infrastructure.domains_included,
            mailboxes_included: client.mailboxes_included || notesData.mailboxes_included || prev.infrastructure.mailboxes_included,
            linkedin_accounts_count: client.linkedin_count || notesData.linkedin_accounts_count || prev.infrastructure.linkedin_accounts_count,
            cold_calling_included: client.cold_calling_included ?? notesData.cold_calling_included ?? prev.infrastructure.cold_calling_included,
            reply_handling: client.reply_handling || notesData.reply_handling || prev.infrastructure.reply_handling
          },
          tam: {
            ...prev.tam,
            tam_accounts_url: client.tam_accounts_url || client.tam_list_url || notesData.tam_accounts_url || prev.tam.tam_accounts_url,
            tam_contacts_url: client.tam_contacts_url || client.blocklist_url || notesData.tam_contacts_url || prev.tam.tam_contacts_url,
            tam_segments: client.tam_segments || notesData.tam_segments || prev.tam.tam_segments,
            tam_uploaded_data: client.tam_uploaded_data || notesData.tam_uploaded_data || prev.tam.tam_uploaded_data
          },
          crm: {
            ...prev.crm,
            crm_system: client.crm_system || notesData.crm_system || prev.crm.crm_system,
            crm_credentials: client.crm_credentials || notesData.crm_credentials || prev.crm.crm_credentials,
            sequencer_system: client.sequencer_system || notesData.sequencer_system || prev.crm.sequencer_system,
            sequencer_credentials: client.sequencer_credentials || notesData.sequencer_credentials || prev.crm.sequencer_credentials,
            enrichment_tools: client.enrichment_tools || notesData.enrichment_tools || prev.crm.enrichment_tools,
            enrichment_credentials: client.enrichment_credentials || notesData.enrichment_credentials || prev.crm.enrichment_credentials
          },
          slack: {
            ...prev.slack,
            slack_main_channel: client.slack_main_channel || prev.slack.slack_main_channel,
            slack_reporting_channel: client.slack_reporting_channel || prev.slack.slack_reporting_channel,
            slack_sales_channel: client.slack_sales_channel || notesData.slack_sales_channel || prev.slack.slack_sales_channel,
            slack_positive_replies_channel: client.slack_positive_replies_channel || notesData.slack_positive_replies_channel || prev.slack.slack_positive_replies_channel,
            slack_neutral_negative_replies_channel: client.slack_neutral_negative_replies_channel || notesData.slack_neutral_negative_replies_channel || prev.slack.slack_neutral_negative_replies_channel
          }
        }))
        
        // Update currentClient state
        setCurrentClient(client)
      }
    }
  }, [client])

  // Calculate actual completion percentage based on filled fields
  const calculateCompletion = (): number => {
    let totalFields = 0
    let filledFields = 0
    
    // Core Info (6 fields)
    totalFields += 6
    if (formData.coreInfo.name?.trim()) filledFields++
    if (formData.coreInfo.company?.trim()) filledFields++
    if (formData.coreInfo.website?.trim()) filledFields++
    if (formData.coreInfo.onboarding_notes?.trim()) filledFields++
    if (formData.coreInfo.package_signed) filledFields++
    if (formData.coreInfo.tam_target_size) filledFields++
    
    // Product (2 fields - products and territories arrays)
    totalFields += 2
    if (formData.product.products?.length > 0) filledFields++
    if (formData.product.territories?.length > 0) filledFields++
    
    // Team (3 key fields)
    totalFields += 3
    if (formData.team.main_contact_email?.trim()) filledFields++
    if (formData.team.team_members?.length > 0) filledFields++
    if (Object.keys(formData.team.meeting_links || {}).length > 0) filledFields++
    
    // Infrastructure (3 fields)
    totalFields += 3
    if (formData.infrastructure.domains_included?.length > 0) filledFields++
    if (formData.infrastructure.mailboxes_included?.length > 0) filledFields++
    if (formData.infrastructure.linkedin_accounts_count && formData.infrastructure.linkedin_accounts_count > 0) filledFields++
    
    // CRM (3 fields)
    totalFields += 3
    if (formData.crm.crm_system?.trim()) filledFields++
    if (formData.crm.sequencer_system?.trim()) filledFields++
    if (formData.crm.enrichment_tools?.length > 0) filledFields++
    
    // Slack (2 main channels)
    totalFields += 2
    if (formData.slack.slack_main_channel?.trim()) filledFields++
    if (formData.slack.slack_reporting_channel?.trim()) filledFields++
    
    // Assets (4 fields)
    totalFields += 4
    if (formData.assets.case_studies_url?.trim()) filledFields++
    if (formData.assets.demo_recordings_url?.trim()) filledFields++
    if (formData.assets.sales_collateral_url?.trim()) filledFields++
    if (formData.assets.other_assets_url?.trim() || formData.assets.assets_notes?.trim()) filledFields++
    
    // TAM (3 fields)
    totalFields += 3
    if (formData.tam.tam_accounts_url?.trim()) filledFields++
    if (formData.tam.tam_contacts_url?.trim()) filledFields++
    if (formData.tam.tam_uploaded_files?.length > 0) filledFields++
    
    return Math.round((filledFields / totalFields) * 100)
  }
  
  const progress = calculateCompletion()
  const currentStepData = STEPS[currentStep]
  const CurrentStepComponent = currentStepData.component

  const handleStepDataChange = (stepId: string, data: any) => {
    setFormData(prev => {
      const currentStepData = prev[stepId as keyof ClientWizardForm] || {}
      // Deep merge for nested objects to preserve all properties
      const merged = { ...currentStepData, ...data }
      
      // Debug logging for all step updates
      if (stepId === 'slack') {
        console.log('[ClientWizard] Updating slack step data:', {
          updates: data,
          currentData: currentStepData,
          mergedData: merged
        })
      }
      
      // Debug logging
      if (stepId === 'product' && (data.products || data.territories)) {
        console.log('[ClientWizard] Updating product step data:', {
          products: data.products?.length || 0,
          territories: data.territories?.length || 0,
          fullData: data
        })
      }
      if (stepId === 'coreInfo') {
        console.log('[ClientWizard] Updating coreInfo step data:', {
          package_signed: data.package_signed,
          tam_target_size: data.tam_target_size,
          fullData: data
        })
      }
      return {
        ...prev,
        [stepId]: merged
      }
    })
  }

  const validateStepCompletion = useCallback((stepIndex: number): boolean => {
    const stepId = STEPS[stepIndex].id
    
    switch (stepId) {
      case 'coreInfo':
        return !!formData.coreInfo.name?.trim()
      
      case 'product':
        return (formData.product.products?.length || 0) > 0 && 
               formData.product.products?.some(p => p.name?.trim())
      
      case 'team':
        return !!formData.team.main_contact_email || 
               (formData.team.team_members?.length || 0) > 0
      
      case 'infrastructure':
        return (formData.infrastructure.domains_included?.length || 0) > 0 || 
               (formData.infrastructure.mailboxes_included?.length || 0) > 0
      
      case 'crm':
        return !!formData.crm.crm_system || 
               !!formData.crm.sequencer_system || 
               (formData.crm.enrichment_tools?.length || 0) > 0
      
      case 'slack':
        return !!formData.slack.slack_main_channel || 
               (formData.slack.slack_sales_channels?.length || 0) > 0
      
      case 'assets':
        // Assets are optional, always considered complete
        return true
      
      case 'tam':
        // TAM is optional, always considered complete
        return true
      
      default:
        return false
    }
  }, [formData])

  // Update completed steps when formData changes
  useEffect(() => {
    const updateCompletedSteps = () => {
      const completed = new Set<number>()
      STEPS.forEach((step, index) => {
        if (validateStepCompletion(index)) {
          completed.add(index)
        }
      })
      setCompletedSteps(completed)
    }
    
    // Debounce validation to avoid excessive re-renders
    const timeoutId = setTimeout(updateCompletedSteps, 300)
    return () => clearTimeout(timeoutId)
  }, [formData, validateStepCompletion])

  const handleNext = async () => {
    // Validate current step before proceeding
    if (currentStep === 0) {
      // Core Info step validation
      const name = formData.coreInfo.name?.trim()
      const website = formData.coreInfo.website?.trim()
      if (!name || !website) {
        toast.error('Please fill in all required fields (Client Name and Website)')
        return
      }
    }
    
    // Auto-save progress before moving to next step
    try {
      await handleSaveDraft()
      toast.success('Progress saved automatically')
    } catch (error) {
      console.error('Auto-save failed:', error)
      toast.error('Failed to save progress. Please try again.')
      return
    }
    
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handlePrevious = async () => {
    // Auto-save progress before going back (in case they made changes)
    try {
      await handleSaveDraft()
      // Don't show success toast for going back, just save silently
    } catch (error) {
      console.error('Auto-save failed:', error)
      // Don't block navigation on save failure, just log it
    }
    
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  // Returns the saved client (updated or newly created) so callers don't
  // depend on setCurrentClient having flushed — reading currentClient right
  // after awaiting this is a stale closure and broke Complete Setup on a
  // brand-new client ("client not found" despite a successful insert).
  const handleSaveDraft = async (): Promise<Client | null> => {
    setIsSaving(true)
    try {
      // Validate required fields
      if (!formData.coreInfo.name || formData.coreInfo.name.trim() === '') {
        toast.error('Client name is required')
        setIsSaving(false)
        return
      }

      // Map form data to database schema - now using dedicated columns
      const clientData: any = {
        // Core Information (dedicated columns)
        name: formData.coreInfo.name.trim(),
        company: formData.coreInfo.company || null,
        website: formData.coreInfo.website || null,
        onboarding_notes: formData.coreInfo.onboarding_notes || null,
        package_signed: formData.coreInfo.package_signed || null,
        tam_target_size: formData.coreInfo.tam_target_size || null,
        clickup_backlog_url: formData.coreInfo.clickup_backlog_url || null,
        attribution_window_days: formData.coreInfo.attribution_window_days || 90,

        // Retainer + lifecycle (migration 20260420150000)
        // An empty/unset value writes NULL so the dashboard renders em-dash.
        monthly_retainer_usd:
          formData.coreInfo.monthly_retainer_usd != null &&
          Number.isFinite(Number(formData.coreInfo.monthly_retainer_usd))
            ? Number(formData.coreInfo.monthly_retainer_usd)
            : null,
        retainer_currency: formData.coreInfo.retainer_currency || null,
        onboarding_date: formData.coreInfo.onboarding_date || null,
        attio_record_id: formData.coreInfo.attio_record_id || null,
        
        // Contact Information (dedicated columns)
        main_contact_email: formData.team.main_contact_email || null,
        phone: formData.team.main_contact_phone || null,
        secondary_contact_email: formData.team.secondary_contact_email || null,
        billing_name: formData.team.billing_name || null,
        billing_email: formData.team.billing_email || null,
        
        // Team Information (dedicated columns)
        sdrs_count: formData.team.sdr_count || null,
        ae_count: formData.team.ae_count || null,
        linkedin_count: formData.infrastructure.linkedin_accounts_count || null,
        meeting_links: formData.team.meeting_links || null,
        sales_team_emails: formData.team.sales_team_emails || null,
        sales_team_names: formData.team.sales_team_names || null,
        sales_team_roles: formData.team.sales_team_roles || null,
        sales_team_regions: formData.team.sales_team_regions || null,
        sales_team_calendly_links: formData.team.sales_team_calendly_links || null,
        
        // Product/Geo Information (dedicated columns)
        products: formData.product.product_names || null,
        geos: formData.product.geos || null,
        product_categories: formData.product.product_categories || null,
        product_description: formData.product.product_description || null,
        icp_titles: formData.product.icp_titles || null,
        icp_departments: formData.product.icp_departments || null,
        compatibility_requirements: formData.product.compatibility_requirements || null,
        suitability_requirements: formData.product.suitability_requirements || null,
        
        // Infrastructure (dedicated columns)
        domains_included: formData.infrastructure.domains_included || null,
        mailboxes_included: formData.infrastructure.mailboxes_included || null,
        cold_calling_included: formData.infrastructure.cold_calling_included || null,
        reply_handling: formData.infrastructure.reply_handling || null,
        
        // TAM & Blocklist (dedicated columns - keep both old and new for compatibility)
        tam_list_url: formData.tam.tam_accounts_url || null,
        blocklist_url: formData.tam.tam_contacts_url || null,
        tam_accounts_url: formData.tam.tam_accounts_url || null,
        tam_contacts_url: formData.tam.tam_contacts_url || null,
        tam_segments: formData.tam.tam_segments || null,
        tam_uploaded_data: formData.tam.tam_uploaded_data || null,
        
        // CRM/Tech Stack (dedicated columns)
        crm_system: formData.crm.crm_system || null,
        crm_credentials: formData.crm.crm_credentials || null,
        sequencer_system: formData.crm.sequencer_system || null,
        sequencer_credentials: formData.crm.sequencer_credentials || null,
        enrichment_tools: formData.crm.enrichment_tools || null,
        enrichment_credentials: formData.crm.enrichment_credentials || null,
        
        // Sync Smartlead Client ID to clients.client_id (numeric field only, NOT the id UUID)
        // If Smartlead is selected and client_id is provided in credentials, use it
        ...(formData.crm.sequencer_system === 'Smartlead' && 
            formData.crm.sequencer_credentials?.client_id ? (() => {
              const smartleadId = parseInt(formData.crm.sequencer_credentials.client_id);
              if (!isNaN(smartleadId)) {
                console.log(`[ClientWizard] Syncing Smartlead Client ID to clients.client_id: ${smartleadId}`);
                return { client_id: smartleadId };
              }
              return {};
            })() : {}),
        
        // Slack Channels (dedicated columns)
        slack_main_channel: formData.slack.slack_main_channel || null,
        slack_reporting_channel: formData.slack.slack_reporting_channel || null,
        slack_sales_channel: formData.slack.slack_sales_channel || null,
        slack_positive_replies_channel: formData.slack.slack_positive_replies_channel || null,
        slack_neutral_negative_replies_channel: formData.slack.slack_neutral_negative_replies_channel || null,
        
        // Status
        status: 'Active',
        
        // Store ONLY complex nested data in notes field (products, territories, team_members, assignments, and other structured arrays)
        notes: JSON.stringify({
          // Team data - contact information (keep in notes for backward compatibility, but also saved to columns)
          main_contact_name: formData.team.main_contact_name,
          main_contact_phone: formData.team.main_contact_phone,
          secondary_contact_name: formData.team.secondary_contact_name,
          secondary_contact_phone: formData.team.secondary_contact_phone,
          // Team members and assignments (structured data - keep in notes as complex nested objects)
          team_members: formData.team.team_members || [],
          assignments: formData.team.assignments || [],
          // Product data - full arrays (complex nested Product and Territory objects - keep in notes)
          products: formData.product.products || [],
          territories: formData.product.territories || [],
          product_names: formData.product.product_names || [],
          // Infrastructure pricing data (keep in notes as it's complex structured data)
          pricing_data: formData.infrastructure.pricing_data,
          // Clay table URLs (keep in notes as these are additional metadata)
          meetings_scheduled_url: formData.tam.meetings_scheduled_url,
          qualified_meetings_url: formData.tam.qualified_meetings_url,
          deals_won_url: formData.tam.deals_won_url,
          accounts_owned_url: formData.tam.accounts_owned_url,
          demos_requested_url: formData.tam.demos_requested_url,
          current_customers_url: formData.tam.current_customers_url,
          customer_signups_url: formData.tam.customer_signups_url,
          churned_customers_url: formData.tam.churned_customers_url,
          dnc_blocklist_url: formData.tam.dnc_blocklist_url,
          // Slack additional (new multi-channel structure - keep in notes for complex nested arrays)
          slack_main_team_member_ids: formData.slack.slack_main_team_member_ids,
          slack_reporting_team_member_ids: formData.slack.slack_reporting_team_member_ids,
          slack_sales_channels: formData.slack.slack_sales_channels || [],
          slack_positive_reply_channels: formData.slack.slack_positive_reply_channels || [],
          slack_neutral_negative_reply_channels: formData.slack.slack_neutral_negative_reply_channels || [],
          // Legacy Slack fields for backward compatibility
          slack_sales_channel: formData.slack.slack_sales_channel,
          slack_positive_replies_channel: formData.slack.slack_positive_replies_channel,
          slack_neutral_negative_replies_channel: formData.slack.slack_neutral_negative_replies_channel,
          // Assets
          case_studies_url: formData.assets.case_studies_url,
          demo_recordings_url: formData.assets.demo_recordings_url,
          sales_collateral_url: formData.assets.sales_collateral_url,
          other_assets_url: formData.assets.other_assets_url,
          assets_notes: formData.assets.assets_notes,
          // Metadata
          updated_by: profile?.email || 'unknown'
        })
      }

      if (currentClient) {
        // Update existing client
        const { error } = await supabase
          .from('clients')
          .update(clientData)
          .eq('id', currentClient.id)

        if (error) {
          console.error('Supabase update error:', error)
          console.error('Error details:', JSON.stringify(error, null, 2))
          console.error('Data being sent:', JSON.stringify(clientData, null, 2))
          throw error
        }
        
        // Sync team members to client_team_members table
        if (formData.team.team_members && formData.team.team_members.length > 0) {
          // Get existing team members from database
          const { data: existingDbMembers } = await supabase
            .from('client_team_members')
            .select('id, email')
            .eq('client_id', currentClient.id)

          const existingDbMemberIds = new Set(existingDbMembers?.map(m => m.id) || [])
          const existingDbMemberEmails = new Set((existingDbMembers?.map(m => m.email?.toLowerCase().trim()) || []).filter(Boolean))
          
          // Prepare members to sync
          const membersToSync = formData.team.team_members.map(member => ({
            id: member.id,
            client_id: currentClient.id,
            name: member.name,
            email: member.email,
            title: (member as any).title || null,
            tags: (member as any).tags || [],
            is_primary_contact: (member as any).is_primary_contact || false,
            is_billing_contact: (member as any).is_billing_contact || false,
            status: 'active' as const,
            // Note: calendly_link column doesn't exist in client_team_members table
            // Calendly links are stored in sales_team_calendly_links array in clients table
          }))

          // Upsert team members (insert or update)
          for (const member of membersToSync) {
            if (existingDbMemberIds.has(member.id)) {
              // Update existing
              const { error: updateError } = await supabase
                .from('client_team_members')
                .update({
                  name: member.name,
                  email: member.email,
                  title: member.title,
                  tags: member.tags,
                  is_primary_contact: member.is_primary_contact,
                  is_billing_contact: member.is_billing_contact,
                  status: member.status,
                  // Note: calendly_link column doesn't exist - stored in clients.sales_team_calendly_links
                  updated_at: new Date().toISOString()
                })
                .eq('id', member.id)
              
              if (updateError) {
                console.error(`Error updating team member ${member.id}:`, updateError)
              }
            } else {
              // Insert new
              const { error: insertError } = await supabase
                .from('client_team_members')
                .insert([member])
              
              if (insertError) {
                console.error(`Error inserting team member ${member.id}:`, insertError)
              }
            }
          }

          // Delete team members that are no longer in the form data
          const formMemberIds = new Set(formData.team.team_members.map(m => m.id))
          const membersToDelete = existingDbMembers?.filter(m => !formMemberIds.has(m.id)) || []
          
          if (membersToDelete.length > 0) {
            const { error: deleteError } = await supabase
              .from('client_team_members')
              .delete()
              .in('id', membersToDelete.map(m => m.id))
            
            if (deleteError) {
              console.error('Error deleting removed team members:', deleteError)
            }
          }

          // Invalidate team members query cache
          queryClient.invalidateQueries({ queryKey: ['client-team-members', currentClient.id] })
        }
        
        // Update local client state with saved data
        setCurrentClient(prev => prev ? { ...prev, ...clientData } : null)
        
        // Invalidate React Query cache so clients list refreshes with updates
        queryClient.invalidateQueries({ queryKey: ['clients'] })
        queryClient.invalidateQueries({ queryKey: ['clients-list'] })
        
        console.log('[ClientWizard] Updated client. Products in formData:', formData.product.products?.length || 0)
        console.log('[ClientWizard] Updated client. Territories in formData:', formData.product.territories?.length || 0)
        
        toast.success('Draft saved successfully')
        return { ...currentClient, ...clientData } as Client
      } else {
        // Create new client - map to database schema
        // Generate a unique numeric client_id (database expects number)
        // Check if Smartlead Client ID is provided first - use it if available
        let numericClientId: number;
        let attempts = 0;
        const maxAttempts = 10;
        let isUnique = false;
        
        // Check if Smartlead Client ID is provided - use it directly
        if (formData.crm.sequencer_system === 'Smartlead' && 
            formData.crm.sequencer_credentials?.client_id) {
          const smartleadId = parseInt(formData.crm.sequencer_credentials.client_id);
          if (!isNaN(smartleadId)) {
            // Verify this ID doesn't already exist
            const { data: existingClient } = await supabase
              .from('clients')
              .select('client_id')
              .eq('client_id', smartleadId)
              .maybeSingle();
            
            if (!existingClient) {
              // Smartlead ID is available and unique - use it
              numericClientId = smartleadId;
              isUnique = true;
              console.log(`[ClientWizard] Using Smartlead Client ID: ${numericClientId}`);
            } else {
              console.warn(`[ClientWizard] Smartlead Client ID ${smartleadId} already exists, generating new ID instead`);
            }
          }
        }
        
        // Only generate new ID if Smartlead ID not provided or already exists
        while (!isUnique && attempts < maxAttempts) {
          attempts++;
          
          // Get max existing client_id (use maybeSingle to handle empty table)
          const { data: maxClientData, error: maxError } = await supabase
            .from('clients')
            .select('client_id')
            .order('client_id', { ascending: false })
            .limit(1)
            .maybeSingle()
          
          const maxClientId = maxClientData?.client_id || 0;
          console.log(`[ClientWizard] Max existing client_id: ${maxClientId}, attempt ${attempts}`)
          
          // Generate new ID: max + 1, or use timestamp-based if max is very high
          if (maxClientId < 999999) {
            numericClientId = maxClientId + 1;
          } else {
            // If max is too high, use timestamp-based with milliseconds for better uniqueness
            const timestamp = Date.now();
            numericClientId = parseInt(timestamp.toString().slice(-6)) + Math.floor(Math.random() * 1000);
          }
          
          // Double-check this ID doesn't exist (race condition protection)
          const { data: existingClient, error: checkError } = await supabase
            .from('clients')
            .select('client_id')
            .eq('client_id', numericClientId)
            .maybeSingle()
          
          // If maybeSingle returns null/undefined and no error (or "not found" error), ID is unique
          if (!existingClient) {
            if (!checkError || checkError.code === 'PGRST116') {
              // No row found - ID is unique!
              isUnique = true;
              console.log(`[ClientWizard] Generated unique client_id: ${numericClientId}`)
              break;
            } else {
              // Real error occurred
              console.warn('[ClientWizard] Error checking client_id uniqueness:', checkError)
            }
          } else {
            // Client exists with this ID - try again
            console.log(`[ClientWizard] client_id ${numericClientId} already exists, trying again...`)
          }
          
          // If we've tried many times, use a more unique timestamp-based approach
          if (attempts >= maxAttempts - 1) {
            // Final fallback: use full timestamp with random component
            const timestamp = Date.now();
            numericClientId = parseInt(timestamp.toString().slice(-9)) + Math.floor(Math.random() * 10000);
            console.log(`[ClientWizard] Using final fallback client_id: ${numericClientId}`)
            // Don't check uniqueness for fallback - just try it
            isUnique = true;
            break;
          }
        }
        
        if (!isUnique) {
          throw new Error('Failed to generate unique client_id after multiple attempts')
        }
        
        const newClientData = {
          id: crypto.randomUUID(), // Generate UUID for id field (required) - NEVER change this
          client_id: numericClientId, // Use Smartlead ID if provided, otherwise generated ID
          ...clientData // Include mapped form data
        }
        
        // Note: client_id is already set correctly above (either Smartlead ID or generated)
        // The spread operator from clientData won't override it since client_id is set explicitly first
        
        console.log('Creating new client with data:', newClientData)
        console.log('[ClientWizard] Generated unique client_id:', numericClientId)
        console.log('[ClientWizard] Saving products:', formData.product.products?.length || 0)
        console.log('[ClientWizard] Saving territories:', formData.product.territories?.length || 0)
        
        const { data, error } = await supabase
          .from('clients')
          .insert(newClientData)
          .select()
          .single()

        let createdClient = data;
        
        if (error) {
          console.error('Supabase insert error:', error)
          console.error('Error details:', JSON.stringify(error, null, 2))
          console.error('Data being sent:', JSON.stringify(newClientData, null, 2))
          
          // If it's a duplicate key error, try one more time with a different ID
          if (error.code === '23505' && (error.message?.includes('client_id') || error.message?.includes('clients_client_id_unique'))) {
            console.log('[ClientWizard] Duplicate client_id detected, retrying with new ID...')
            console.log('[ClientWizard] Attempted client_id:', numericClientId)
            
            // Generate a completely new ID using timestamp + milliseconds + random
            const timestamp = Date.now();
            const fallbackClientId = parseInt(timestamp.toString().slice(-9)) + Math.floor(Math.random() * 10000);
            
            console.log('[ClientWizard] Retrying with fallback client_id:', fallbackClientId)
            
            const retryClientData = {
              ...newClientData,
              client_id: fallbackClientId
            }
            
            const { data: retryData, error: retryError } = await supabase
              .from('clients')
              .insert(retryClientData)
              .select()
              .single()
            
            if (retryError) {
              console.error('[ClientWizard] Retry also failed:', retryError)
              console.error('[ClientWizard] Retry error details:', JSON.stringify(retryError, null, 2))
              
              // If retry also fails with duplicate, try one more time with even more randomness
              if (retryError.code === '23505' && (retryError.message?.includes('client_id') || retryError.message?.includes('clients_client_id_unique'))) {
                console.log('[ClientWizard] Second retry also had duplicate, trying one more time...')
                const finalClientId = parseInt(Date.now().toString()) + Math.floor(Math.random() * 100000);
                const finalRetryData = {
                  ...newClientData,
                  client_id: finalClientId
                }
                
                const { data: finalData, error: finalError } = await supabase
                  .from('clients')
                  .insert(finalRetryData)
                  .select()
                  .single()
                
                if (finalError) {
                  console.error('[ClientWizard] Final retry also failed:', finalError)
                  throw finalError
                }
                
                createdClient = finalData
                console.log('[ClientWizard] Successfully created client with final fallback client_id:', finalClientId)
              } else {
                throw retryError
              }
            } else {
              createdClient = retryData
              console.log('[ClientWizard] Successfully created client with fallback client_id:', fallbackClientId)
            }
          } else {
            throw error
          }
        }
        
        // Store the created client so subsequent saves update instead of creating new ones
        setCurrentClient(createdClient)
        
        // Sync team members to client_team_members table for new client
        if (formData.team.team_members && formData.team.team_members.length > 0) {
          const membersToSync = formData.team.team_members.map(member => ({
            id: member.id,
            client_id: createdClient.id,
            name: member.name,
            email: member.email,
            title: (member as any).title || null,
            tags: (member as any).tags || [],
            is_primary_contact: (member as any).is_primary_contact || false,
            is_billing_contact: (member as any).is_billing_contact || false,
            status: 'active' as const,
            // Note: calendly_link column doesn't exist in client_team_members table
            // Calendly links are stored in sales_team_calendly_links array in clients table
          }))

          const { error: insertError } = await supabase
            .from('client_team_members')
            .insert(membersToSync)
          
          if (insertError) {
            console.error('Error inserting team members for new client:', insertError)
          } else {
            // Invalidate team members query cache
            queryClient.invalidateQueries({ queryKey: ['client-team-members', createdClient.id] })
          }
        }
        
        // Invalidate React Query cache so clients list refreshes immediately
        queryClient.invalidateQueries({ queryKey: ['clients'] })
        queryClient.invalidateQueries({ queryKey: ['clients-list'] })
        
        toast.success('Client created successfully')

        // Don't call onComplete here during auto-save, only on final completion
        // onComplete?.(data)
        return createdClient as Client
      }
    } catch (error) {
      console.error('Error saving draft:', error)
      toast.error('Failed to save draft')
      return null
    } finally {
      setIsSaving(false)
    }
    return null
  }

  const handleComplete = async () => {
    setIsLoading(true)
    try {
      const savedClient = await handleSaveDraft()
      setCompletedSteps(prev => new Set([...prev, currentStep]))
      // Prefer the return value: currentClient in this closure is stale when
      // the save just created the client.
      const completedClient = savedClient ?? currentClient
      if (completedClient) {
        toast.success('Client setup completed successfully!')
        onComplete?.(completedClient)
      } else {
        console.error('No client to complete')
        toast.error('Failed to complete: client not found')
      }
    } catch (error) {
      console.error('Error completing setup:', error)
      toast.error('Failed to complete setup')
    } finally {
      setIsLoading(false)
    }
  }

  const isStepCompleted = (stepIndex: number) => completedSteps.has(stepIndex)
  const isLastStep = currentStep === STEPS.length - 1

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">
          {currentClient ? 'Edit Client' : 'Create New Client'}
        </h1>
        <p className="text-muted-foreground">
          Complete the client setup process step by step
        </p>
      </div>

      {/* Progress */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Step {currentStep + 1} of {STEPS.length}</span>
              <div className="flex items-center space-x-2">
                {isSaving && (
                  <span className="text-xs text-primary flex items-center">
                    <Save className="h-3 w-3 mr-1 animate-pulse" />
                    Auto-saving...
                  </span>
                )}
                <span>{Math.round(progress)}% Complete</span>
              </div>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </CardContent>
      </Card>

      {/* Step Navigation */}
      <div className="grid grid-cols-8 gap-1.5">
        {STEPS.map((step, index) => {
          const Icon = step.icon
          const isCompleted = isStepCompleted(index)
          const isCurrent = index === currentStep
          
          return (
            <button
              key={step.id}
              onClick={() => setCurrentStep(index)}
              className={`p-2.5 rounded-lg border transition-colors ${
                isCurrent 
                  ? 'border-primary bg-primary/10 text-primary' 
                  : isCompleted 
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-muted hover:border-primary/50'
              }`}
            >
              <div className="flex flex-col items-center space-y-1.5">
                <div className="relative">
                  <Icon className="h-5 w-5" />
                  {isCompleted && (
                    <Check className="h-3 w-3 absolute -top-1 -right-1 text-green-600" />
                  )}
                </div>
                <span className="text-xs font-medium text-center leading-tight">
                  {step.title}
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Current Step */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <currentStepData.icon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>{currentStepData.title}</CardTitle>
              <CardDescription>{currentStepData.description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <CurrentStepComponent
            data={formData[currentStepData.id as keyof ClientWizardForm]}
            onChange={(data) => handleStepDataChange(currentStepData.id, data)}
            isLoading={isLoading}
            client={currentClient}
            tamSize={formData.coreInfo.tam_target_size}
            {...(currentStepData.id === 'infrastructure' && { 
              clientWebsite: formData.coreInfo.website,
              clientName: formData.coreInfo.name || client?.name 
            })}
            {...(currentStepData.id === 'team' && { productData: { products: formData.product.products || [], territories: formData.product.territories || [] } })}
            {...(currentStepData.id === 'slack' && { 
              productData: { products: formData.product.products || [], territories: formData.product.territories || [] },
              teamMembers: formData.team.team_members || []
            })}
          />
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <div className="flex space-x-2">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={isSaving}
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Draft'}
          </Button>
        </div>

        <div className="flex space-x-2">
          {currentStep > 0 && (
            <Button variant="outline" onClick={handlePrevious} disabled={isSaving}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>
          )}
          
          {isLastStep ? (
            <Button onClick={handleComplete} disabled={isLoading}>
              <Check className="h-4 w-4 mr-2" />
              {isLoading ? 'Completing...' : 'Complete Setup'}
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={isSaving}>
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
